import React, { useEffect, useState } from 'react';
import { getClients, createClient, updateClient, deleteClient } from '../../services/api';
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaSpinner } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const emptyClient = {
  rut: '', nombre: '', email: '', razon_social: '', giro: '', direccion: '',
  fecha_facturacion: '', estado_pago: 'al_dia', tipo_contrato: 'basico',
  proxima_fecha_facturacion: '', estado: 'activo'
};

const ClientsPage = ({ token }) => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyClient);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadData = async () => {
    const res = await getClients(token);
    if (res.cod === 401) {
      localStorage.removeItem('token');
      navigate('/login');
      return;
    }
    if (res.cod === 200) setClients(res.data);
  };

  useEffect(() => { loadData(); }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        const res = await updateClient(token, editing, form);
        if (res.cod !== 200) {
          setError(res.message || 'No se pudo actualizar el cliente');
          return;
        }
      } else {
        const res = await createClient(token, form);
        if (res.cod !== 201) {
          setError(res.message || 'No se pudo crear el cliente');
          return;
        }
      }
      setError('');
      setShowForm(false);
      setEditing(null);
      setForm(emptyClient);
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (c) => {
    setForm({ ...emptyClient, ...c });
    setEditing(c.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este cliente?')) return;
    await deleteClient(token, id);
    loadData();
  };

  const filtered = clients.filter(c =>
    !search || c.nombre?.toLowerCase().includes(search.toLowerCase()) ||
    c.rut?.toLowerCase().includes(search.toLowerCase()) ||
    c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const estados = ['activo', 'inactivo'];
  const tiposPago = ['al_dia', 'pendiente', 'atrasado'];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
          <p className="text-sm text-gray-500 mt-1">{clients.length} cliente{clients.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => { setForm(emptyClient); setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm">
          <FaPlus className="w-3.5 h-3.5" /> Nuevo Cliente
        </button>
      </div>

      <div className="relative mb-4">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          placeholder="Buscar por nombre, RUT o email..." />
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
          {error}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => { setShowForm(false); setEditing(null); }}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">{editing ? 'Editar Cliente' : 'Nuevo Cliente'}</h2>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <FaTimes className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: 'rut', label: 'RUT' }, { key: 'nombre', label: 'Nombre' },
                 { key: 'email', label: 'Email' }, { key: 'razon_social', label: 'Razón Social' },
                 { key: 'giro', label: 'Giro' }, { key: 'direccion', label: 'Dirección' },
                 { key: 'fecha_facturacion', label: 'Fecha Fact.', type: 'date' },
                 { key: 'proxima_fecha_facturacion', label: 'Próx. Fact.', type: 'date' },
              ].map(({ key, label, type }) => (
                <div key={key} className="flex flex-col">
                  <label className="text-xs font-medium text-gray-500 mb-1">{label}</label>
                  <input name={key} type={type || 'text'} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none" />
                </div>
              ))}
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-500 mb-1">Estado Pago</label>
                <select value={form.estado_pago} onChange={e => setForm({ ...form, estado_pago: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
                  {tiposPago.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                </select>
              </div>
              <div className="flex flex-col">
                <label className="text-xs font-medium text-gray-500 mb-1">Estado</label>
                <select value={form.estado} onChange={e => setForm({ ...form, estado: e.target.value })}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 outline-none">
                  {estados.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowForm(false); setEditing(null); }}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shadow-sm">
                {saving && <FaSpinner className="w-3.5 h-3.5 animate-spin" />}
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">ID</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">RUT</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Nombre</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider hidden md:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider hidden lg:table-cell">Razón Social</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Estado</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Pago</th>
                <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{c.id}</td>
                  <td className="px-4 py-3 font-medium">{c.rut}</td>
                  <td className="px-4 py-3">{c.nombre}</td>
                  <td className="px-4 py-3 text-gray-500 hidden md:table-cell">{c.email}</td>
                  <td className="px-4 py-3 text-gray-500 hidden lg:table-cell">{c.razon_social || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.estado === 'activo' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {c.estado === 'activo' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      c.estado_pago === 'al_dia' ? 'bg-green-50 text-green-700' :
                      c.estado_pago === 'pendiente' ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'
                    }`}>
                      {c.estado_pago.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleEdit(c)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Editar">
                      <FaEdit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(c.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-1" title="Eliminar">
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-8 text-center text-gray-400 text-sm">
            <FaSearch className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            {search ? 'No se encontraron clientes con ese criterio' : 'No hay clientes registrados'}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsPage;
